import { Controller } from "@hotwired/stimulus"

const SNAP_POINTS = [0, 0.33, 0.67]
const STORAGE_KEY = "map-panel-ratio"

export default class extends Controller {
  static targets = [
    "mapPanel",
    "telemetryPanel",
    "divider",
    "handle",
    "hoverZone",
    "snapButton",
  ]

  connect() {
    this._ratio = this._loadRatio()
    this._dragging = false
    this._containerRect = null
    this._peekAnimation = null

    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)

    this._resizeObserver = new ResizeObserver(() => this._applyLayout())
    this._resizeObserver.observe(this.element)

    this._applyLayout()
    this._updateSnapButtons()
  }

  disconnect() {
    this._resizeObserver?.disconnect()
    clearTimeout(this._peekHideTimer)
    window.removeEventListener("mousemove", this._onMouseMove)
    window.removeEventListener("mouseup", this._onMouseUp)
  }

  // --- Drag handling ---

  startDrag(event) {
    event.preventDefault()
    this._dragging = true
    this._dragStartX = event.clientX
    this._dragMoved = false
    this._ratioAtDragStart = this._ratio
    this._containerRect = this.element.getBoundingClientRect()

    // Cancel peek animation if running
    if (this._peekAnimation) {
      this._peekAnimation.cancel()
      this._peekAnimation = null
    }

    // Remove transition during drag
    this.mapPanelTarget.style.transition = "none"
    this.telemetryPanelTarget.style.transition = "none"
    this.dividerTarget.style.transition = "none"

    window.addEventListener("mousemove", this._onMouseMove)
    window.addEventListener("mouseup", this._onMouseUp)
  }

  _onMouseMove(event) {
    if (!this._dragging || !this._containerRect) return

    if (!this._dragMoved && Math.abs(event.clientX - this._dragStartX) > 3) {
      this._dragMoved = true
    }

    const containerRight = this._containerRect.right
    const containerWidth = this._containerRect.width
    const distFromRight = containerRight - event.clientX

    let ratio = distFromRight / containerWidth
    ratio = Math.max(0, Math.min(0.67, ratio))

    // Collapse if too small
    if (ratio < 0.15) ratio = 0

    this._ratio = ratio
    this._applyLayout()
  }

  _onMouseUp() {
    if (!this._dragging) return
    this._dragging = false

    window.removeEventListener("mousemove", this._onMouseMove)
    window.removeEventListener("mouseup", this._onMouseUp)

    // Click without drag while collapsed → snap to 1/3
    if (!this._dragMoved && this._ratioAtDragStart === 0) {
      this._ratio = SNAP_POINTS[1]
      this._saveRatio(this._ratio)
      this._updateSnapButtons()
      this._peekVisible = false
      this.dividerTarget.style.position = ""
      this.dividerTarget.style.right = ""
      this.dividerTarget.style.zIndex = ""

      this.mapPanelTarget.style.transition = "width 200ms ease-out"
      this.telemetryPanelTarget.style.transition =
        "width 200ms ease-out, opacity 200ms ease-out"
      this.dividerTarget.style.transition = "opacity 200ms ease-out"
      this._applyLayout()
      setTimeout(() => {
        this.mapPanelTarget.style.transition = ""
        this.telemetryPanelTarget.style.transition = ""
        this.dividerTarget.style.transition = ""
      }, 220)
      return
    }

    // Snap to nearest point
    this._ratio = this._snapToNearest(this._ratio)
    this._saveRatio(this._ratio)
    this._updateSnapButtons()

    // Animate to snap point
    this.mapPanelTarget.style.transition = "width 200ms ease-out"
    this.telemetryPanelTarget.style.transition =
      "width 200ms ease-out, opacity 200ms ease-out"
    this.dividerTarget.style.transition = "opacity 200ms ease-out"

    this._applyLayout()

    // Remove transition after animation
    setTimeout(() => {
      this.mapPanelTarget.style.transition = ""
      this.telemetryPanelTarget.style.transition = ""
      this.dividerTarget.style.transition = ""
    }, 220)
  }

  _snapToNearest(ratio) {
    let nearest = SNAP_POINTS[0]
    let minDist = Infinity

    for (const snap of SNAP_POINTS) {
      const dist = Math.abs(ratio - snap)
      if (dist < minDist) {
        minDist = dist
        nearest = snap
      }
    }

    return nearest
  }

  // --- Preset ratio buttons ---

  setRatio(event) {
    const ratio = parseFloat(event.params.ratio)
    if (isNaN(ratio)) return

    // Clear peek state if going from collapsed
    if (this._ratio === 0 && ratio > 0) {
      this._peekVisible = false
      this.dividerTarget.style.position = ""
      this.dividerTarget.style.right = ""
      this.dividerTarget.style.zIndex = ""
    }

    this._ratio = ratio
    this._saveRatio(ratio)
    this._updateSnapButtons()

    this.mapPanelTarget.style.transition = "width 200ms ease-out"
    this.telemetryPanelTarget.style.transition =
      "width 200ms ease-out, opacity 200ms ease-out"
    this.dividerTarget.style.transition = "opacity 200ms ease-out"
    this._applyLayout()
    setTimeout(() => {
      this.mapPanelTarget.style.transition = ""
      this.telemetryPanelTarget.style.transition = ""
      this.dividerTarget.style.transition = ""
    }, 220)
  }

  // --- Hover-to-reveal ---

  // Both the hover zone and the divider itself keep the peek visible.
  // A short delay on leave prevents flicker when moving between them.

  onPeekEnter() {
    if (this._ratio > 0 || this._dragging) return
    clearTimeout(this._peekHideTimer)
    this._peekHideTimer = null

    if (this._peekVisible) return
    this._peekVisible = true

    // Show divider with peek animation
    this.dividerTarget.style.display = ""
    this.dividerTarget.style.position = "absolute"
    this.dividerTarget.style.right = "0"
    this.dividerTarget.style.top = "0"
    this.dividerTarget.style.bottom = "0"
    this.dividerTarget.style.zIndex = "20"

    this._peekAnimation = this.dividerTarget.animate(
      [
        { transform: "translateX(100%)", opacity: 0 },
        { transform: "translateX(-2px)", opacity: 1 },
        { transform: "translateX(0)", opacity: 1 },
      ],
      { duration: 250, easing: "ease-out", fill: "forwards" },
    )
  }

  onPeekLeave() {
    if (this._ratio > 0 || this._dragging) return

    // Delay hide so moving from hover zone → divider doesn't flicker
    clearTimeout(this._peekHideTimer)
    this._peekHideTimer = setTimeout(() => {
      this._peekHideTimer = null
      if (this._dragging || this._ratio > 0) return
      this._peekVisible = false

      const fadeOut = this.dividerTarget.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: 150, easing: "ease-out", fill: "forwards" },
      )
      fadeOut.onfinish = () => {
        if (this._ratio === 0 && !this._dragging && !this._peekVisible) {
          this.dividerTarget.style.display = "none"
          this.dividerTarget.style.position = ""
          this.dividerTarget.style.right = ""
          this.dividerTarget.style.top = ""
          this.dividerTarget.style.bottom = ""
          this.dividerTarget.style.zIndex = ""
        }
      }
    }, 80)
  }

  // --- Layout ---

  _applyLayout() {
    const isCollapsed = this._ratio === 0
    const dividerWidth = 15

    if (isCollapsed) {
      this.mapPanelTarget.style.width = "100%"
      this.telemetryPanelTarget.style.width = "0"
      this.telemetryPanelTarget.style.opacity = "0"
      this.telemetryPanelTarget.style.overflow = "hidden"
      if (!this._dragging) {
        this.dividerTarget.style.display = "none"
      }
      // Restore full rounding when collapsed
      this.mapPanelTarget.style.borderRadius = ""
      this.telemetryPanelTarget.style.borderRadius = ""
    } else {
      const containerWidth = this.element.clientWidth
      const telemetryWidth = Math.round(containerWidth * this._ratio)
      const mapWidth = containerWidth - telemetryWidth - dividerWidth

      this.mapPanelTarget.style.width = `${mapWidth}px`
      this.telemetryPanelTarget.style.width = `${telemetryWidth}px`
      this.telemetryPanelTarget.style.opacity = "1"
      this.telemetryPanelTarget.style.overflow = ""
      this.dividerTarget.style.display = ""
      this.dividerTarget.style.position = ""
      this.dividerTarget.style.right = ""
      this.dividerTarget.style.zIndex = ""
      // Square off inner corners adjacent to the divider
      this.mapPanelTarget.style.borderRadius = "0.5rem 0 0 0.5rem"
      this.telemetryPanelTarget.style.borderRadius = "0 0.5rem 0.5rem 0"
    }
  }

  // --- Snap button active state ---

  _updateSnapButtons() {
    if (!this.hasSnapButtonTarget) return
    this.snapButtonTargets.forEach((btn) => {
      const btnRatio = parseFloat(btn.dataset.mapPanelRatioParam)
      const isActive = Math.abs(btnRatio - this._ratio) < 0.01
      btn.classList.toggle("text-white", isActive)
      btn.classList.toggle("bg-white/5", isActive)
      btn.classList.toggle("text-white/60", !isActive)
    })
  }

  // --- Persistence ---

  _loadRatio() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored != null) {
        const val = parseFloat(stored)
        if (!isNaN(val) && val >= 0 && val <= 0.67) return val
      }
    } catch (_) {
      /* ignore */
    }
    return SNAP_POINTS[1]
  }

  _saveRatio(ratio) {
    try {
      sessionStorage.setItem(STORAGE_KEY, ratio.toString())
    } catch (_) {
      /* ignore */
    }
  }
}
